import type { Product } from "../../shared/products.ts";

export type Creation = {
  slug: string;
  userName: string;
  birthDate: string;
  zodiacSign: string;
  reading: string;
  candleName: string;
  theme: string;
  scent: { top: string; heart: string; base: string };
  dedication: string;
  createdAt: number;
};

export type CreateCreationInput = {
  name: string;
  birthDate: string;
  email: string;
};

export type RequestKind = "order" | "gift";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function parseError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body.error ?? `${res.status} ${res.statusText}`;
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}

export async function createCreation(
  input: CreateCreationInput,
): Promise<{ slug: string }> {
  const res = await fetch("/api/creations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new ApiError(await parseError(res), res.status);
  }
  return (await res.json()) as { slug: string };
}

export async function getCreation(slug: string): Promise<Creation> {
  const res = await fetch(`/api/creations/${encodeURIComponent(slug)}`);
  if (!res.ok) {
    throw new ApiError(await parseError(res), res.status);
  }
  return (await res.json()) as Creation;
}

export async function requestCreationAction(
  slug: string,
  kind: RequestKind,
): Promise<void> {
  const res = await fetch(
    `/api/creations/${encodeURIComponent(slug)}/request`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind }),
    },
  );
  if (!res.ok) {
    throw new ApiError(await parseError(res), res.status);
  }
}

export async function createCreationCheckout(
  slug: string,
): Promise<{ url: string; sessionId: string }> {
  const res = await fetch(
    `/api/creations/${encodeURIComponent(slug)}/checkout`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ returnOrigin: window.location.origin }),
    },
  );
  if (!res.ok) {
    throw new ApiError(await parseError(res), res.status);
  }
  return (await res.json()) as { url: string; sessionId: string };
}

export type CheckoutLineItem = {
  slug: string;
  name: string;
  quantity: number;
  unitAmount: number;
  currency: string;
};

export type CheckoutSession = {
  sessionId: string;
  status: string | null;
  paymentStatus: string | null;
  amountTotal: number | null;
  currency: string | null;
  email: string | null;
  items: CheckoutLineItem[];
};

export type CheckoutItemInput = {
  slug: string;
  quantity: number;
  creationSlug?: string;
};

export async function createCheckoutSession(
  items: CheckoutItemInput[],
): Promise<{ url: string; sessionId: string }> {
  const res = await fetch("/api/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items,
      returnOrigin: window.location.origin,
    }),
  });
  if (!res.ok) {
    throw new ApiError(await parseError(res), res.status);
  }
  return (await res.json()) as { url: string; sessionId: string };
}

export async function getCheckoutSession(
  sessionId: string,
): Promise<CheckoutSession> {
  const res = await fetch(
    `/api/checkout/session/${encodeURIComponent(sessionId)}`,
  );
  if (!res.ok) {
    throw new ApiError(await parseError(res), res.status);
  }
  return (await res.json()) as CheckoutSession;
}

export async function listProducts(): Promise<Product[]> {
  const res = await fetch("/api/products");
  if (!res.ok) {
    throw new ApiError(await parseError(res), res.status);
  }
  const body = (await res.json()) as { products: Product[] };
  return body.products;
}
